import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Building2, Briefcase, GraduationCap, Check } from 'lucide-react';
import { labSetupFeatures } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LabSetup = () => {
  const [formData, setFormData] = useState({ clinicName: '', contactName: '', email: '', phone: '', clinicSize: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/inquiries/lab`, formData);
      toast.success(response.data.message);
      setFormData({ clinicName: '', contactName: '', email: '', phone: '', clinicSize: '', message: '' });
    } catch (error) {
      console.error('Lab inquiry error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="lab-setup" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
            <span className="bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">3D Lab Setup</span>
            <span className="block mt-2 text-gray-900">For Your Clinic</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join the Hybionics network and bring accessible, custom prosthetic care to your community. Complete setup, training, and ongoing support included.
          </p>
        </div>

        {/* Hero Image */}
        <div className="relative mb-16 rounded-2xl overflow-hidden shadow-2xl">
          <img
            src="https://images.unsplash.com/photo-1612886649464-4a888608eace"
            alt="3D Lab Setup"
            className="w-full h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-blue-900/80 flex items-center justify-center">
            <div className="text-center text-white space-y-4 px-4">
              <h3 className="text-3xl md:text-4xl font-bold">Transform Your Practice</h3>
              <p className="text-lg md:text-xl max-w-2xl mx-auto">
                Become a pioneer in accessible prosthetic care with our turnkey 3D lab solution
              </p>
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 mt-4">
                    Request Setup Information
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>3D Lab Setup Inquiry</DialogTitle>
                    <DialogDescription>
                      Tell us about your clinic and we'll help you get started.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="clinicName">Clinic Name</Label>
                      <Input
                        id="clinicName"
                        value={formData.clinicName}
                        onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactName">Contact Person</Label>
                      <Input
                        id="contactName"
                        value={formData.contactName}
                        onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clinicSize">Clinic Size</Label>
                      <Select onValueChange={(value) => setFormData({ ...formData, clinicSize: value })} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Select clinic size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small">Small (1-5 staff)</SelectItem>
                          <SelectItem value="medium">Medium (6-15 staff)</SelectItem>
                          <SelectItem value="large">Large (16+ staff)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Additional Information</Label>
                      <Textarea
                        id="message"
                        rows={3}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {labSetupFeatures.map((feature) => (
            <div
              key={feature.id}
              className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="mb-4">
                {feature.id === 1 && <Building2 className="w-12 h-12 text-purple-600" />}
                {feature.id === 2 && <GraduationCap className="w-12 h-12 text-purple-600" />}
                {feature.id === 3 && <Briefcase className="w-12 h-12 text-purple-600" />}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 mb-6">{feature.description}</p>
              <ul className="space-y-2">
                {feature.includes.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-gray-700">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-purple-600 to-blue-500 rounded-2xl p-12 text-white shadow-2xl">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your Clinic?</h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Join 50+ partner clinics already providing accessible, custom prosthetic care to their communities.
            </p>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6">
                  Schedule a Consultation
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>3D Lab Setup Inquiry</DialogTitle>
                  <DialogDescription>
                    Tell us about your clinic and we'll help you get started.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="clinicName2">Clinic Name</Label>
                    <Input
                      id="clinicName2"
                      value={formData.clinicName}
                      onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactName2">Contact Person</Label>
                    <Input
                      id="contactName2"
                      value={formData.contactName}
                      onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email2">Email</Label>
                    <Input
                      id="email2"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone2">Phone Number</Label>
                    <Input
                      id="phone2"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clinicSize2">Clinic Size</Label>
                    <Select onValueChange={(value) => setFormData({ ...formData, clinicSize: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select clinic size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Small (1-5 staff)</SelectItem>
                        <SelectItem value="medium">Medium (6-15 staff)</SelectItem>
                        <SelectItem value="large">Large (16+ staff)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message2">Additional Information</Label>
                    <Textarea
                      id="message2"
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LabSetup;